import { ReactElement, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { signedDistanceInDirectionOf } from '@geometry'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { useSceneLineMarker } from '@editorHooks'

interface OpeningWithWall {
  opening: Opening
  wall: ShapeObject
  indexInWall: number
}

interface SingleOpeningAlignMarkersProps {
  opening: Opening
  wall: ShapeObject
  globalStart: { x: number; y: number; z: number }
  globalEnd: { x: number; y: number; z: number }
  color: string
}

const SingleOpeningAlignMarkers = ({
  opening,
  wall,
  globalStart,
  globalEnd,
  color,
}: SingleOpeningAlignMarkersProps): null => {
  const [p0, , p2] = opening.shape.points
  const projectedGlobalStart = useMemo(() => {
    const wallStart = wall.shape.points[0]
    const wallEnd = wall.shape.points[1]
    const wallDirection = wallEnd.sub(wallStart).normalize()
    const wallLength = wallStart.distanceTo(wallEnd)
    if (wallLength === 0) {
      return {
        x: globalStart.x,
        y: globalStart.y,
        z: globalStart.z,
      }
    }

    const globalStartPoint = new ImmutableVector3(globalStart.x, globalStart.y, globalStart.z)
    const signedDistanceFromWallStart = signedDistanceInDirectionOf(
      wallStart,
      globalStartPoint,
      wallDirection,
    )
    const projectedPoint = wallStart.addScaledVector(wallDirection, signedDistanceFromWallStart)
    return {
      x: projectedPoint.x,
      y: projectedPoint.y,
      z: globalStart.z,
    }
  }, [globalStart.x, globalStart.y, globalStart.z, wall.shape.points])
  const projectedGlobalEnd = useMemo(() => {
    const wallStart = wall.shape.points[0]
    const wallEnd = wall.shape.points[1]
    const wallDirection = wallEnd.sub(wallStart).normalize()
    const wallLength = wallStart.distanceTo(wallEnd)
    if (wallLength === 0) {
      return {
        x: globalEnd.x,
        y: globalEnd.y,
        z: globalEnd.z,
      }
    }

    const globalEndPoint = new ImmutableVector3(globalEnd.x, globalEnd.y, globalEnd.z)
    const signedDistanceFromWallStart = signedDistanceInDirectionOf(
      wallStart,
      globalEndPoint,
      wallDirection,
    )
    const projectedPoint = wallStart.addScaledVector(wallDirection, signedDistanceFromWallStart)
    return {
      x: projectedPoint.x,
      y: projectedPoint.y,
      z: globalEnd.z,
    }
  }, [globalEnd.x, globalEnd.y, globalEnd.z, wall.shape.points])
  const derivedStart = useMemo(
    () => ({ x: projectedGlobalStart.x, y: projectedGlobalStart.y, z: p0.z }),
    [projectedGlobalStart.x, projectedGlobalStart.y, p0.z],
  )
  const derivedEnd = useMemo(
    () => ({ x: projectedGlobalEnd.x, y: projectedGlobalEnd.y, z: p2.z }),
    [projectedGlobalEnd.x, projectedGlobalEnd.y, p2.z],
  )
  const rectangleLines = useMemo(
    () => [
      [derivedStart, { x: derivedEnd.x, y: derivedEnd.y, z: derivedStart.z }],
      [{ x: derivedEnd.x, y: derivedEnd.y, z: derivedStart.z }, derivedEnd],
      [derivedEnd, { x: derivedStart.x, y: derivedStart.y, z: derivedEnd.z }],
      [{ x: derivedStart.x, y: derivedStart.y, z: derivedEnd.z }, derivedStart],
    ],
    [derivedStart, derivedEnd],
  )

  const enabled =
    typeof globalStart.x === 'number' &&
    typeof globalStart.y === 'number' &&
    typeof globalEnd.x === 'number' &&
    typeof globalEnd.y === 'number'

  useSceneLineMarker({
    lines: rectangleLines,
    enabled,
    color,
    lineWidth: 5,
    renderOrder: 1000,
    disableDepthTest: true,
  })

  return null
}

interface AlignSceneMarkersProps {
  openingsWithWalls: OpeningWithWall[]
  colorByOpeningGuid: Record<string, string>
}

const AlignSceneMarkers = ({
  openingsWithWalls,
  colorByOpeningGuid,
}: AlignSceneMarkersProps): ReactElement | null => {
  const globalStart = useWatch({ name: 'globalStart' }) as
    | { x: number; y: number; z: number }
    | undefined
  const globalEnd = useWatch({ name: 'globalEnd' }) as
    | { x: number; y: number; z: number }
    | undefined

  if (
    !globalStart ||
    !globalEnd ||
    typeof globalStart.x !== 'number' ||
    typeof globalStart.y !== 'number' ||
    typeof globalEnd.x !== 'number' ||
    typeof globalEnd.y !== 'number'
  ) {
    return null
  }

  return (
    <>
      {openingsWithWalls.map(({ opening, wall }) => (
        <SingleOpeningAlignMarkers
          key={opening.guid}
          opening={opening}
          wall={wall}
          globalStart={globalStart}
          globalEnd={globalEnd}
          color={colorByOpeningGuid[opening.guid] ?? 'grey'}
        />
      ))}
    </>
  )
}

export default AlignSceneMarkers
